import { Tag } from '../../Tags/types';
import { Layout } from 'react-grid-layout';
import { ListProperty } from '../../../components/CustomProperties/ListProperties/types';
import { MediaItem } from '../../../components/ZuulMediaLibrary';

export interface Property {
  id?: number;
  replacementKey: string;
  label: string;
  type: PropertyType;
  description: string;
  createdAt: Date;
  updatedAt: Date;
  tags: Tag[];
  additionalConfiguration: AdditionalConfiguration;
}

type AdditionalConfiguration =
  ( MultiSelectConfig
  | DropdownConfig
  | RadioConfig
  | ImageConfig
  | {})
  & CommonConfiguration;

export interface MultiSelectConfig {
  fieldValues: string[];
}

export interface DropdownConfig {
  fieldValues: string[];
}

export interface RadioConfig {
  fieldValues: string[];
  icons?: Record<string, string>; // map value -> bootstrap icon class (e.g., 'bi-heart')
}

export interface ImageConfig {
  defaultValue: {
    id: string;
    altText: string;
    mediaItem: MediaItem | null;
  };
}

export interface CommonConfiguration {
  defaultValue: any;
}

export const isMultiSelect = (property: Property): property is Property & { additionalConfiguration: MultiSelectConfig } => {
  return property.type === "Multi-select";
}

export const isDropdown = (property: Property): property is Property & { additionalConfiguration: DropdownConfig } => {
  return property.type === "Dropdown";
}

export const isRadio = (property: Property): property is Property & { additionalConfiguration: RadioConfig } => {
  return property.type === "Radio";
}

export const isImage = (property: Property): property is Property & { additionalConfiguration: ImageConfig } => {
  return property.type === "Image";
}

export const isDropdownOrMultiSelect = (
  property: Property
): property is Property & { additionalConfiguration: DropdownConfig | MultiSelectConfig | RadioConfig } => {
  return property.type === "Multi-select" || property.type === "Dropdown" || property.type === "Radio";
}

export const PROPERTY_TYPES = [
  "Single line Text",
  "Multi-select",
  "Boolean",
  "Color",
  "Multi-line Text",
  "Dropdown",
  "Image",
  "WYSIWYG",
  "Radio",
  "",
] as const;

export const PROPERTY_TYPES_DROPDOWN = [
  "Single line Text",
  "Multi-select",
  "Boolean",
  "Color",
  "Multi-line Text",
  "Dropdown",
  "Image",
  "WYSIWYG",
  "Radio"
] as const;


export type PropertyType = typeof PROPERTY_TYPES[number];

export const propertyDefaults : Property = {
  replacementKey: '',
  label: '',
  type: '',
  description: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  tags: [],
  additionalConfiguration: {
    defaultValue: '',
  },
};

export const getDefaultValue = (type: PropertyType): any => {
  switch (type) {
    case "Single line Text":
      return '';
    case "Multi-line Text":
      return '';
    case "WYSIWYG":
      return '';
    case "Multi-select":
      return [];
    case "Dropdown":
      return '';
    case "Radio":
      return '';
    case "Boolean":
      return false;
    case "Color":
      return '';
    case "Image":
      return { id: '', altText: '', mediaItem: null };
    default:
      return '';
  }
}

export const getDefaultPropertySize = (property: Property): Layout => {
  const id = 'property-key-' + property.replacementKey;
  switch (property.type) {
    case "Single line Text":
      return { i:id, w: 4, h: 2, minW:2, minH:2, x: 0, y: Infinity };
    case "Multi-line Text":
      return { i:id, w: 6, h: 2, minW:2, minH:2, x: 0, y: Infinity };
    case "WYSIWYG":
      return { i:id, w: 6, h: 3, minW:3, minH:2, x: 0, y: Infinity };
    case "Multi-select":
      return { i:id, w: 4, h: 2, minW:2, minH:2, x: 0, y: Infinity };
    case "Dropdown":
      return { i:id, w: 4, h: 2, minW:2, minH:2, x: 0, y: Infinity };
    case "Radio":
      return { i:id, w: 4, h: 2, minW:2, minH:2, x: 0, y: Infinity };
    case "Boolean":
      return { i:id, w: 2, h: 1, minW:2, minH:1, x: 0, y: Infinity };
    case "Color":
      return { i:id, w: 2, h: 1, minW:2, minH:1, x: 0, y: Infinity };
    case "Image":
      return { i:id, w: 6, h: 4, minW:4, minH:3, x: 0, y: Infinity };
    default:
      return { i:id, w: 4, h: 2, minW:2, minH:2, x: 0, y: Infinity };
  }
}

export const getDefaultListPropertySize = (listProperty: ListProperty): Layout => {
  const id = 'list-property-key-' + listProperty.replacementKey;
  return { i:id, w: 4, h: 4, minW: 0, minH: 0, x: 0, y: Infinity };
}
